import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Card, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import { fetchUser } from '../../api/user-api';
import styles from './ClientPanels.module.scss';

const STATUS = {
  ACTIVE: { label: 'Aktivan', color: 'green' },
  INACTIVE: { label: 'Neaktivan', color: 'default' },
  PENDING_VERIFICATION: { label: 'Čeka verifikaciju', color: 'gold' },
  SUSPENDED: { label: 'Suspendovan', color: 'red' },
  DELETED: { label: 'Obrisan', color: 'red' },
};

const LANGUAGE = {
  sr: 'Srpski',
  en: 'Engleski',
  hu: 'Mađarski',
  de: 'Nemački',
};

/**
 * Basic client info. The API decides what comes back: without data:contact:read it leaves
 * out email and phone, and this card then says so instead of showing empty fields.
 */
const ClientProfileCard = ({ userId, canSeeContact }) => {
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchUser(userId).then(({ data, success, message }) => {
      if (cancelled) return;
      if (success === false) setError(message || 'Profil nije dostupan.');
      else setUser(data);
    });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  if (error) {
    return (
      <Card size="small" className={styles.panel} title="Profil">
        <Typography.Text type="secondary">{error}</Typography.Text>
      </Card>
    );
  }
  if (!user) return null;

  const status = STATUS[user.status] || {
    label: user.status,
    color: 'default',
  };
  const field = (label, value) => (
    <div>
      <span className={styles.panel__label}>{label}</span>
      <span className={styles.panel__value}>{value || '-'}</span>
    </div>
  );

  return (
    <Card
      size="small"
      className={styles.panel}
      title={
        <span className={styles.panel__title}>
          {[user.firstName, user.lastName].filter(Boolean).join(' ') ||
            'Profil'}
        </span>
      }
      extra={<Tag color={status.color}>{status.label}</Tag>}
    >
      <div className={styles.panel__grid}>
        {field(
          'Član od',
          user.createdAt ? dayjs(user.createdAt).format('DD.MM.YYYY.') : null
        )}
        {field('VIP', user.vip?.status === 'active' ? 'Da' : 'Ne')}
        {field('Jezik aplikacije', LANGUAGE[user.preferredLanguage] || null)}
        {field('Mentor', user.referredByDoctor ? 'Da' : 'Ne')}
        {canSeeContact && field('Email', user.email)}
        {canSeeContact && field('Telefon', user.phone)}
      </div>
      {!canSeeContact && (
        <Typography.Text type="secondary" className={styles.panel__note}>
          Kontakt podaci su sakriveni: vaša rola nema pristup kontakt podacima.
        </Typography.Text>
      )}
    </Card>
  );
};

ClientProfileCard.propTypes = {
  userId: PropTypes.string.isRequired,
  canSeeContact: PropTypes.bool,
};

export default ClientProfileCard;
